/**
 * Mirrors NourishmentEngine's DailyResultsAdminTriggers payloads (yt-admin/v1/daily-results). Scores are 0..1
 * decimals; dates are `yyyy-MM-dd` strings; categories carry the MaiDy scoring category id.
 */
export type DailyResultStatus = 'Scored' | 'NoConsumption' | 'Unzoned' | 'TargetsUnavailable';

export type NutrientPlanType = 'Unspecified' | 'Prescriptive' | 'Phenotype';

export interface DailyCategoryResultV1 {
  category: number;
  score: number;
  scoreLow?: number;
  scoreHigh?: number;
  priorityFactor: number;
  weightedScore: number;
  standardDeviation: number;
  nutrientCount: number;
}

export interface DailyNutrientResultV1 {
  nutrientId: string;
  category: number;
  score: number;
  quantity: number;
  dailyMin: number;
  dailyOptimal: number;
  dailyMax: number;
  importanceFactor: number;
  weightedScore: number;
}

export interface DailyResultV1 {
  youtopianId: string;
  localDate?: string;
  groupId?: string;
  status: DailyResultStatus;
  finalScore: number;
  finalScoreLow?: number;
  finalScoreHigh?: number;
  preliminaryScore: number;
  energyBalance: number;
  energyMultiplier: number;
  standardDeviation: number;
  algorithmVersion: number;
  planType: NutrientPlanType;
  planEffectiveDate?: string | null;
  scoredAtUtc?: string;
  categories: DailyCategoryResultV1[];
  nutrients: DailyNutrientResultV1[];
  overageRampCollapsedNutrientIds?: string[];
}

/** An explicit range echoes `from` and `to`; the look-back window answers `lookbackDays` instead. */
export interface DailyResultsRescoreResponseV1 {
  youtopianId: string;
  from?: string;
  to?: string;
  lookbackDays?: number;
  marked: number;
}

/** One local day in the panel's list: its stored result, or null when the engine has not scored it. */
export interface DailyResultDay {
  date: string;
  result: DailyResultV1 | null;
}

export interface DailyResultsBacklogRowV1 {
  groupId: string;
  youtopianId: string;
  localDate: string;
  markedAtUtc: string;
  lockedUntil?: string | null;
  attemptCount: number;
  lastFailureTransient: boolean;
  lastFailureReason?: string | null;
}

export interface DailyResultsBacklogV1 {
  rows: DailyResultsBacklogRowV1[];
}

/**
 * One Youtopian's days still waiting in the rescore queue. The engine lists the queue worst first and bounds the
 * page, so a full page that names none of their days cannot say whether they are waiting further down.
 */
export interface DailyResultsQueueState {
  pending: DailyResultsBacklogRowV1[];
  truncated: boolean;
}

export interface DailyResultsRescoreRange {
  from: string;
  to: string;
}

/** The engine's own bound on an explicit rescore range, both ends inclusive. */
export const MAX_RESCORE_RANGE_DAYS = 92;

export interface NutrientQuantityV1 {
  nutrientId: string;
  quantity: number;
}

export interface DailyResultsSimulationRequestV1 {
  quantities: NutrientQuantityV1[];
}

const CATEGORY_NAMES: Record<number, string> = {
  4: 'N2MIN',
  5: 'CDEN',
  6: 'RDEN',
  7: 'RDEBN',
  8: 'Water',
  10: 'CDEN Protein',
  11: 'CDEN Fiber',
  12: 'CDEN Vitamins & Minerals',
  13: 'CDEN Omega-3'
};

export const scoringCategoryName = (category: number): string => CATEGORY_NAMES[category] ?? `Category ${category}`;

export type DailyResultSeverity = 'success' | 'info' | 'warn' | 'danger' | 'secondary';

const STATUS_SEVERITY: Record<DailyResultStatus, DailyResultSeverity> = {
  Scored: 'success',
  NoConsumption: 'secondary',
  Unzoned: 'warn',
  TargetsUnavailable: 'danger'
};

export const dailyResultSeverity = (status: DailyResultStatus): DailyResultSeverity =>
  STATUS_SEVERITY[status] ?? 'info';

// The stored day carries no bounds, so they are reckoned from the standard deviation when absent.
export const dailyResultBand = (result: DailyResultV1): string => {
  const low = result.finalScoreLow ?? Math.max(0, result.finalScore - result.standardDeviation);
  const high = result.finalScoreHigh ?? Math.min(1, result.finalScore + result.standardDeviation);
  return `${low.toFixed(3)} to ${high.toFixed(3)}`;
};

// The engine writes the stamp with no zone marker, so it is shown as the UTC wall clock it is rather than parsed,
// which would read it as this browser's local time.
export const utcStamp = (stamp: string): string => `${stamp.slice(0, 19).replace('T', ' ')} UTC`;
